import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  OutlinedInput,
  InputLabel,
  MenuItem,
  FormControl,
  ListItemText,
  Select,
  Checkbox,
  Box,
  Chip,
  IconButton,
  Stack,
  Typography,
  Tooltip,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import SearchWithDebounce from "./SearchWithDebounce";
import { FixedSizeList } from "react-window";
import { TooltipProps } from "../../../helpers/materialUITheme";
import CheckBox from "../icons/CheckBox";
import CheckBoxChecked from "../icons/CheckBoxChecked";
import { Arrow } from "../icons/Arrow";

const VISIBLE_ITEMS = 4;
const ITEM_HEIGHT = 60;
const ITEM_PADDING_TOP = 30;
const SEARCH_HEIGHT = 72;
const CHIP_CONTAINER_MAX_HEIGHT = 100;

export const SelectMultipleWithClientSearch = ({
  options,
  label,
  value,
  onChange,
  error,
  labelKey = "name",
  displayEmpty = false,
  placeholder,
  disabled = false,
  isIds = false,
  showSelectAll = false, // Новый параметр
}) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options || []);
  const [selectedValues, setSelectedValues] = useState(isIds ? value || [] : value || []);
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectWidth, setSelectWidth] = useState("auto");
  const ref = useRef();
  const resizeObserverRef = useRef(null);
  const selectRef = React.useRef(null);

  useEffect(() => {
    if (selectRef.current) {
      setSelectWidth(selectRef.current.clientWidth);
    }
  }, []);
  useEffect(() => {
    if (selectRef.current) {
      // Инициализируем ResizeObserver для отслеживания изменений размера
      resizeObserverRef.current = new ResizeObserver((entries) => {
        for (let entry of entries) {
          setSelectWidth(entry.contentRect.width);
        }
      });
      resizeObserverRef.current.observe(selectRef.current);
    }
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    setFilteredOptions(options || []);
  }, [options]);

  useEffect(() => {
    if (searchValue) {
      const filtered = (options || []).filter((option) => {
        const optionText = (option.name?.toString() || option?.[labelKey] || option || "").toLowerCase();
        return optionText.includes(searchValue.toLowerCase());
      });
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions(options || []);
    }
  }, [searchValue, options, labelKey]);

  useEffect(() => {
    setSelectedValues(isIds ? value || [] : value || []);
  }, [value, isIds]);

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setSearchValue("");
  };

  const handleDelete = (e, value) => {
    e.preventDefault();
    e.stopPropagation();
    let requestData;
    if (isIds) {
      requestData = selectedValues.filter((id) => id !== value.id);
    } else {
      requestData = selectedValues.filter((ee) => ee?.id !== value?.id);
    }
    setSelectedValues(requestData);
    onChange(requestData);
  };

  const handleClearAll = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const newValue = [];
    setSelectedValues(newValue);
    onChange(newValue);
  };

  // Новая функция для обработки выбора всех элементов
  const handleSelectAll = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (isIds) {
      const allIds = filteredOptions.map((option) => option.id);
      const newValue = selectedValues.length === allIds.length ? [] : allIds;
      setSelectedValues(newValue);
      onChange(newValue);
    } else {
      const newValue = selectedValues.length === filteredOptions.length ? [] : [...filteredOptions];
      setSelectedValues(newValue);
      onChange(newValue);
    }
  };

  const isChecked = (option) => {
    if (isIds) {
      return selectedValues.includes(option.id);
    }
    return selectedValues.some((item) => item?.id === option?.id);
  };

  const allSelected = () => {
    if (isIds) {
      return filteredOptions.length > 0 && filteredOptions.every((option) => selectedValues.includes(option.id));
    }
    return (
      filteredOptions.length > 0 &&
      filteredOptions.every((option) => selectedValues.some((item) => item?.id === option?.id))
    );
  };

  const renderMenuItems = useCallback(
    ({ index, style }) => {
      // Если включен showSelectAll и это первый элемент, рендерим "Выбрать все"
      if (showSelectAll && index === 0) {
        const allSelectedValue = allSelected();
        return (
          <MenuItem
            key="select-all"
            style={style}
            sx={{ display: "flex", alignItems: "center", fontWeight: 600 }}
            onClick={handleSelectAll}
          >
            <Checkbox
              checked={allSelectedValue}
              sx={{ mr: "8px", borderRadius: 0 }}
              icon={<CheckBox />}
              checkedIcon={<CheckBoxChecked />}
            />
            <ListItemText primary={t("Выбрать все")} />
          </MenuItem>
        );
      }

      // Корректируем индекс, если есть пункт "Выбрать все"
      const adjustedIndex = showSelectAll ? index - 1 : index;
      const option = filteredOptions[adjustedIndex];
      if (!option) return null;

      const checked = isChecked(option);
      const text = option.name?.toString() || option?.[labelKey] || option || "";

      // Рассчитываем доступную ширину (ширина Select - отступы - padding - чекбокс)
      const availableWidth = selectWidth - 40 - 16 - 24;

      // Создаем временный span для измерения текста
      const measureTextWidth = (text, font = "16px Inter") => {
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        context.font = font;
        return context.measureText(text).width;
      };

      const textWidth = measureTextWidth(text);
      const isOverflowed = textWidth > availableWidth;

      return (
        <MenuItem
          key={option.id || adjustedIndex}
          value={isIds ? option.id : option}
          style={{ ...style, width: selectWidth - 40 }}
          sx={{ display: "flex", alignItems: "center" }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();

            let newValue;
            if (isIds) {
              if (checked) {
                newValue = selectedValues.filter((id) => id !== option.id);
              } else {
                newValue = [...selectedValues, option.id];
              }
            } else {
              if (checked) {
                newValue = selectedValues.filter((item) => item.id !== option.id);
              } else {
                newValue = [...selectedValues, option];
              }
            }

            setSelectedValues(newValue);
            onChange(newValue);
          }}
        >
          <Checkbox
            checked={checked}
            sx={{ mr: "8px", borderRadius: 0 }}
            icon={<CheckBox />}
            checkedIcon={<CheckBoxChecked />}
          />
          {isOverflowed ? (
            <Tooltip title={text} followCursor componentsProps={TooltipProps} placement="bottom" arrow>
              <ListItemText
                primary={text}
                primaryTypographyProps={{
                  noWrap: true,
                  style: {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: availableWidth,
                  },
                }}
              />
            </Tooltip>
          ) : (
            <ListItemText
              primary={text}
              primaryTypographyProps={{
                noWrap: true,
                style: {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
              }}
            />
          )}
        </MenuItem>
      );
    },
    [filteredOptions, selectedValues, isIds, labelKey, showSelectAll, allSelected]
  );

  const MenuProps = {
    PaperProps: {
      style: {
        width: selectWidth,
        maxHeight:
          options?.length >= 4
            ? (ITEM_HEIGHT + ITEM_PADDING_TOP) * 4 + SEARCH_HEIGHT
            : (ITEM_HEIGHT + ITEM_PADDING_TOP) * options?.length + SEARCH_HEIGHT,
      },
    },
  };

  const getDisplayValues = () => {
    if (isIds) {
      return selectedValues.map((id) => {
        return options?.find((option) => option.id === id) || id;
      });
    }
    return selectedValues;
  };

  const itemCount = showSelectAll ? filteredOptions.length + 1 : filteredOptions.length;
  return (
    <Box sx={{ position: "relative" }} className="input_text">
      <InputLabel sx={{ mb: "8px" }}>{t(label)}</InputLabel>
      <FormControl fullWidth error={!!error}>
        <Box ref={selectRef}>
          <Select
            ref={ref}
            multiple
            value={selectedValues}
            disabled={disabled}
            displayEmpty={displayEmpty}
            onChange={() => {}} // Пустой обработчик, так как мы управляем выбором через MenuItem
            input={<OutlinedInput />}
            sx={{
              ".MuiSelect-select": {
                overflow: "hidden",
              },
            }}
            open={isOpen}
            onOpen={handleOpen}
            onClose={handleClose}
            IconComponent={() =>
              !disabled && (
                <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                  {selectedValues?.length > 0 && (
                    <IconButton size="small" onClick={handleClearAll} sx={{ p: 0 }}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  )}
                  <Box
                    sx={{ cursor: "pointer" }}
                    onClick={() => {
                      setIsOpen((prev) => !prev);
                    }}
                  >
                    {isOpen ? <Arrow /> : <Arrow />}
                  </Box>
                </Stack>
              )
            }
            renderValue={(selected) => {
              const displayValues = getDisplayValues();

              return displayValues?.length > 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.5,
                    maxHeight: CHIP_CONTAINER_MAX_HEIGHT,
                    overflowY: "auto",
                    pr: 1,
                  }}
                >
                  {displayValues?.map((valueObj, i) => {
                    const label =
                      valueObj?.name ||
                      valueObj?.[labelKey] ||
                      filteredOptions.find((item) => item.id === valueObj.id)?.name ||
                      valueObj;
                    return !disabled ? (
                      <Chip
                        key={i}
                        label={label}
                        clickable
                        deleteIcon={<ClearIcon onMouseDown={(event) => event.stopPropagation()} />}
                        onDelete={(e) => handleDelete(e, valueObj)}
                      />
                    ) : (
                      <Chip key={i} label={label} clickable />
                    );
                  })}
                </Box>
              ) : (
                t(placeholder) || "Нет"
              );
            }}
            MenuProps={MenuProps}
          >
            <SearchWithDebounce searchValue={searchValue} setSearchValue={setSearchValue} />
            {filteredOptions.length > 0 ? (
              <FixedSizeList
                height={Math.min(itemCount, VISIBLE_ITEMS) * ITEM_HEIGHT}
                itemCount={itemCount}
                itemSize={ITEM_HEIGHT}
                width={selectWidth}
                style={{ overflowX: "hidden" }}
              >
                {renderMenuItems}
              </FixedSizeList>
            ) : (
              <MenuItem disabled sx={{ height: ITEM_HEIGHT }}>
                <ListItemText primary={<Typography>{"Нет результатов"}</Typography>} />
              </MenuItem>
            )}
          </Select>
        </Box>
      </FormControl>
    </Box>
  );
};
